import React from "react";
import { useRouter } from "next/router";

type OfferCta =
  | { kind: "link"; href: string }
  | { kind: "action"; onClick: () => void | Promise<void> };

export type OfferPopupBannerProps = {
  offerId: string;
  repeatDaily?: boolean;
  topOffsetPx?: number;
  enabled?: boolean;

  headline: string;
  subheadline?: string;
  body?: string;

  hero?: { src: string; alt: string };

  ctaLabel: string;
  cta: OfferCta;

  stickyText?: string;
  showStickyBeforeSeen?: boolean;

  className?: string;
};

function todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function storageKey(offerId: string, repeatDaily: boolean) {
  return repeatDaily ? `bt_offer_seen:${offerId}:${todayKey()}` : `bt_offer_seen:${offerId}`;
}

export default function OfferPopupBanner(props: OfferPopupBannerProps) {
  const router = useRouter();

  const {
    offerId,
    repeatDaily = true,
    topOffsetPx = 0,
    enabled = true,
    headline,
    subheadline,
    body,
    hero,
    ctaLabel,
    cta,
    stickyText,
    showStickyBeforeSeen = false,
    className,
  } = props;

  const [mounted, setMounted] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [hasSeen, setHasSeen] = React.useState(false);
  const [isWorking, setIsWorking] = React.useState(false);

  const key = React.useMemo(() => storageKey(offerId, repeatDaily), [offerId, repeatDaily]);

  React.useEffect(() => {
    setMounted(true);
    if (!enabled) return;

    try {
      const seen = window.localStorage.getItem(key) === "1";
      setHasSeen(seen);
      setIsModalOpen(!seen);
    } catch {
      setHasSeen(false);
      setIsModalOpen(true);
    }
  }, [enabled, key]);

  const markSeen = React.useCallback(() => {
    try {
      window.localStorage.setItem(key, "1");
    } catch {
      // ignore
    }
    setHasSeen(true);
  }, [key]);

  const closeModal = React.useCallback(() => {
    markSeen();
    setIsModalOpen(false);
  }, [markSeen]);

  const runCta = React.useCallback(async () => {
    if (isWorking) return;
    setIsWorking(true);

    try {
      markSeen();
      setIsModalOpen(false);

      if (cta.kind === "link") {
        // Use client-side navigation for internal links, fallback to full nav for external
        const isExternal = /^https?:\/\//i.test(cta.href);
        if (isExternal) window.location.href = cta.href;
        else await router.push(cta.href);
      } else {
        await cta.onClick();
      }
    } finally {
      setIsWorking(false);
    }
  }, [cta, isWorking, markSeen, router]);

  // prevent SSR mismatch / flash
  if (!mounted || !enabled) return null;

  const showSticky = showStickyBeforeSeen ? true : hasSeen;

  return (
    <>
      {/* Sticky collapsed bar */}
      {showSticky && (
        <div className={["sticky left-0 right-0 z-5", className ?? ""].join(" ")} style={{ top: topOffsetPx }}>
          <button
            type="button"
            onClick={runCta}
            className={[
              "w-full",
              "bg-[#3f2415] text-[#fffaeb]",
              "border-b border-black/10",
              "px-4 py-2",
              "flex items-center justify-center gap-2",
              "text-sm md:text-base",
              "hover:opacity-95 active:opacity-90",
            ].join(" ")}
            aria-label="Open offer"
          >
            <span className="font-semibold">{stickyText ?? headline}</span>
            <span className="opacity-90">•</span>
            <span className="underline underline-offset-2">{ctaLabel}</span>
          </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/55"
            onClick={closeModal}
            aria-label="Close offer"
          />

          {/* Panel */}
          <div className="relative w-[92vw] max-w-[720px] rounded-2xl bg-[#fffaeb] shadow-2xl overflow-hidden">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-3 top-3 rounded-full bg-black/5 px-3 py-1 text-sm hover:bg-black/10"
              aria-label="Close"
            >
              ✕
            </button>

            {hero?.src && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={hero.src} alt={hero.alt} className="h-[220px] w-full object-cover" />
            )}

            <div className="p-6 md:p-8">
              <div className="space-y-2">
                <h2 className="text-2xl md:text-3xl font-black text-[#3f2415]">{headline}</h2>

                {subheadline && (
                  <p className="text-base md:text-lg font-semibold text-[#603026]">{subheadline}</p>
                )}

                {body && <p className="text-sm md:text-base text-[#603026] leading-relaxed">{body}</p>}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={runCta}
                  disabled={isWorking}
                  className={[
                    "rounded-xl px-5 py-3 font-bold",
                    "bg-[#da3334] text-[#fffaeb]",
                    "hover:opacity-95 active:opacity-90",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                  ].join(" ")}
                >
                  {isWorking ? "Opening…" : ctaLabel}
                </button>

                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl px-5 py-3 font-semibold bg-black/5 text-[#3f2415] hover:bg-black/10"
                >
                  Not now
                </button>
              </div>

              <div className="mt-4 text-xs text-[#603026]/75">
                You’ll only see this once {repeatDaily ? "per day" : "per offer"}.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
